import type { RefObject } from "preact";
import { useEffect } from "preact/hooks";

type Dir = "left" | "right" | "up" | "down";

const ARROWS: Readonly<Record<string, Dir>> = {
  ArrowLeft: "left",
  ArrowRight: "right",
  ArrowUp: "up",
  ArrowDown: "down",
};

const TEXT_INPUT_TYPES: Readonly<Record<string, true>> = {
  text: true,
  password: true,
  search: true,
  email: true,
  url: true,
  tel: true,
};

/** Whether the focused control needs this arrow key for itself. */
function controlOwnsKey(el: Element, dir: Dir): boolean {
  // Selects change their option with every arrow key; number fields step with Up/Down.
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) {
    if (el.type === "number") return true;
    if (el.type === "range") return dir === "left" || dir === "right";
    if (TEXT_INPUT_TYPES[el.type]) return dir === "left" || dir === "right";
  }
  return el.getAttribute("role") === "slider";
}

function isNavigable(el: HTMLElement): boolean {
  if (el.hasAttribute("disabled") || el.getAttribute("aria-disabled") === "true") return false;
  return el.offsetParent !== null || el.getClientRects().length > 0;
}

/** Best candidate in the direction of travel: nearest along the axis, penalizing sideways drift. */
function pickNext(current: HTMLElement, candidates: HTMLElement[], dir: Dir): HTMLElement | null {
  const from = current.getBoundingClientRect();
  const fx = from.left + from.width / 2;
  const fy = from.top + from.height / 2;
  let best: HTMLElement | null = null;
  let bestScore = Infinity;
  for (const el of candidates) {
    if (el === current) continue;
    const r = el.getBoundingClientRect();
    const dx = r.left + r.width / 2 - fx;
    const dy = r.top + r.height / 2 - fy;
    const along = dir === "right" ? dx : dir === "left" ? -dx : dir === "down" ? dy : -dy;
    const across = dir === "left" || dir === "right" ? Math.abs(dy) : Math.abs(dx);
    if (along < 2) continue;
    const score = along + across * 2.5;
    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }
  return best;
}

/**
 * Arrow-key navigation between the `[data-nav]` elements of a container (menu lists, card
 * grids). Moves focus to the nearest element in the pressed direction. Text fields, selects and
 * sliders keep the arrow keys they need.
 */
export function useArrowNav(ref: RefObject<HTMLElement | null>, selector = "[data-nav]"): void {
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const dir = ARROWS[e.key];
      if (!dir || e.defaultPrevented || e.isComposing || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !root.contains(active)) return;
      if (controlOwnsKey(active, dir)) return;
      const current = active.closest<HTMLElement>(selector) ?? active;
      const candidates = Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(isNavigable);
      const next = pickNext(current, candidates, dir);
      if (next) {
        e.preventDefault();
        next.focus();
      }
    };
    root.addEventListener("keydown", onKeyDown);
    return () => root.removeEventListener("keydown", onKeyDown);
  }, [ref, selector]);
}
